/**
 * The note sections a user typed into the protocol wizard, drawn onto the
 * sheet. Its own module because it shares nothing with the issue table, the
 * activity log or the comments block beyond the sheet and the pacer.
 */
import { wrapText } from "@/domains/documents/utils/templateFormPdfRenderer";
import {
  CONTENT_WIDTH,
  MARGIN,
  capLines,
  drawLines,
  drawRule,
  drawText,
  ensureSpace,
  linesPerPage,
  type PdfSheet,
} from "@/domains/pm/utils/protocolPdfSheet";
import type { ProtocolDocumentModel, ProtocolDocumentNote } from "@/domains/pm/utils/protocol";
import type { Pacer } from "@/domains/pm/utils/protocolPdfPacer";

const NOTE_HEADING_SIZE = 11.5;
const NOTE_HEADING_LINE_HEIGHT = 14;
/** A note heading's own chrome: the rule above it (18) and the gap under it (6). */
const NOTE_HEADING_CHROME = 24;
const NOTE_BODY_SIZE = 9.5;
const NOTE_BODY_LINE_HEIGHT = 13;
/** The gap under a paragraph. Small on purpose: people type attendee and
 *  decision lists one line at a time, and a full blank line between each would
 *  break a list into a page of stragglers. */
const NOTE_PARAGRAPH_GAP = 4;

function noteBodyLines(sheet: PdfSheet, paragraph: string): string[] {
  return wrapText(paragraph, sheet.regular, NOTE_BODY_SIZE, CONTENT_WIDTH);
}

function noteParagraphHeight(sheet: PdfSheet, paragraph: string): number {
  return noteBodyLines(sheet, paragraph).length * NOTE_BODY_LINE_HEIGHT + NOTE_PARAGRAPH_GAP;
}

/**
 * A note heading and the first paragraph under it, measured as one block, for
 * the same reason an activity block is: a heading is meaningless without what
 * it introduces, and reserving it alone strands it at the foot of a page. The
 * heading is capped against the FIRST paragraph's measured height, because
 * `ensureSpace` silently caps any reservation at one page — a block reserved
 * taller than that is not reserved at all.
 */
function noteBlock(
  sheet: PdfSheet,
  note: ProtocolDocumentNote
): { heading: string[]; height: number } {
  const first = note.paragraphs[0];
  const firstHeight = first ? noteParagraphHeight(sheet, first) : 0;
  const heading = capLines(
    wrapText(note.heading, sheet.bold, NOTE_HEADING_SIZE, CONTENT_WIDTH),
    sheet.bold,
    NOTE_HEADING_SIZE,
    CONTENT_WIDTH,
    linesPerPage(NOTE_HEADING_LINE_HEIGHT, NOTE_HEADING_CHROME + firstHeight)
  );
  return {
    heading,
    height: NOTE_HEADING_CHROME + heading.length * NOTE_HEADING_LINE_HEIGHT + firstHeight,
  };
}

/**
 * A paragraph, drawn one line at a time.
 *
 * Deliberately NOT `drawWrapped`: that caps at a single page and ends the
 * overflow in an ellipsis, which is right for an issue title someone pasted a
 * description into and wrong here — this is the one block whose entire content
 * is the user's own words, and losing its tail to a "…" is silent data loss in
 * a filed document. Breaking per line paginates instead of clipping, and gives
 * the pacer somewhere to yield inside a long note.
 */
async function drawNoteParagraph(sheet: PdfSheet, paragraph: string, pace: Pacer): Promise<void> {
  for (const line of noteBodyLines(sheet, paragraph)) {
    ensureSpace(sheet, NOTE_BODY_LINE_HEIGHT);
    drawText(sheet, line, MARGIN, sheet.y - NOTE_BODY_SIZE, { size: NOTE_BODY_SIZE });
    sheet.y -= NOTE_BODY_LINE_HEIGHT;
    await pace();
  }
  sheet.y -= NOTE_PARAGRAPH_GAP;
}

/**
 * The sections the user wrote. Unlike every other block here, an empty list
 * genuinely means "there are none" — a note exists only because somebody typed
 * it — so this is the one place the empty-array guard is honest.
 */
export async function drawNotes(
  sheet: PdfSheet,
  model: ProtocolDocumentModel,
  pace: Pacer
): Promise<void> {
  if (model.notes.length === 0) return;
  for (const note of model.notes) {
    const { heading, height } = noteBlock(sheet, note);
    ensureSpace(sheet, height);
    drawRule(sheet, sheet.y);
    sheet.y -= 18;
    sheet.y -=
      drawLines(sheet, heading, MARGIN, sheet.y, {
        font: sheet.bold,
        size: NOTE_HEADING_SIZE,
        lineHeight: NOTE_HEADING_LINE_HEIGHT,
      }) + 6;
    for (const paragraph of note.paragraphs) {
      await drawNoteParagraph(sheet, paragraph, pace);
    }
    sheet.y -= 8;
  }
  sheet.y -= 8;
}
